import breakpoints from '@/breakpoints';
import { LinkButton } from '@/components/elements/forms/buttons';
import Fab from '@/components/elements/forms/buttons/Fab/Fab';
import Icon from '@/components/icons/Icon';
import { APP_ROUTES, ROUTE_BUILDER } from '@/constants/pages';
import { classnames } from '@/helpers';
import { DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION } from '@/hooks/search/useSearch.constants';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { Fragment, MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';

import NavLink from '@/components/elements/NavLink';
import { SEARCH_CONTEXT } from '@/features/searchV2/constants/tracking';
import { useAppSelector } from '@/hooks';
import { User } from '@/types/user';
import { twMerge } from 'tailwind-merge';
import * as megaMenuHelpers from '../helpers';
import { TAB_VISIBILITY_BREAKPOINT } from '../MegaMenu.constants';
import { TabIdentifier, UseMegaMenuManagerOutput } from '../MegaMenu.types';
import {
  BEAUTY_SUB_CATEGORIES,
  GIFTCARD_SUB_CATEGORIES,
  HAIRDRESSER_SUB_CATEGORIES,
  HEALTH_SUB_CATEGORIES,
  LASHES_SUB_CATEGORIES,
  MASSAGE_SUB_CATEGORIES,
  MORE_CATEGORIES_GROUPED,
  MORE_CATEGORY_KEYS,
  MoreCategoryKey,
  NAILS_SUB_CATEGORIES,
  SISTAMINUTEN_SUB_CATEGORIES,
  SKINCARE_SUB_CATEGORIES,
  WELLNESSCARD_SUB_CATEGORIES,
} from './Category.constants';
import { getDefaultSEOContentCtaLink } from './Category.helpers';
import { MegaMenuSubCategory } from './Category.types';

export const baseTranslationKey = 'features.mega-menu.modules.Category';

function translate(key: string) {
  return _s(`${baseTranslationKey}.${key}`);
}

function getCategoryClassnames(active: boolean) {
  return classnames(
    'container-layout bg-surface_l2 overflow-y-auto bg-surface_l2',
    'rounded-bl-md rounded-br-md border-border_regular',
    !active && 'absolute inset-0 h-0 -z-10',
    active && 'h-full p-lg md:p-3xl border border-t-0',
  );
}

function splitCategoryLinksIntoColumns(
  links: MegaMenuSubCategory['links'],
  maxLinksPerColumn = 5,
): MegaMenuSubCategory['links'][] {
  const columns = Math.ceil(links.length / maxLinksPerColumn);
  const rows = Array.from({ length: columns }, (_, index) =>
    links.slice(index * maxLinksPerColumn, index * maxLinksPerColumn + maxLinksPerColumn),
  );
  return rows;
}

function handleOnCategoryLinkClick(
  e: MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement>,
  identifier: TabIdentifier,
  item: MegaMenuSubCategory['links'][number],
  history: History,
  match: any /** no types for routeMatch api with out react-rouder-dom version */,
  user: User,
  cb: () => void,
) {
  megaMenuHelpers.handleOnCategoryLinkClick(e, item.to, history, cb);
  megaMenuHelpers.trackMegaMenuClick(identifier, item, Boolean(user), match);
}

const Category = ({
  isActive,
  identifier,
  children,
  className,
}: {
  children: ReactNode;
  isActive: boolean;
  identifier: TabIdentifier;
  className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isActive) {
      ref.current?.scrollTo({ top: 0, behavior: 'smooth' });
      ref.current?.focus();
    }

    return () => {
      if (isActive) {
        ref.current?.blur();
      }
    };
  }, [isActive]);

  return (
    <Fragment>
      <nav
        ref={ref}
        id={`category-${identifier}`}
        className={twMerge(getCategoryClassnames(isActive), className)}
        tabIndex={-1}
        aria-label={translate('aria-label.' + identifier)}>
        {children}
      </nav>
    </Fragment>
  );
};

const CategoryContent = (
  props: UseMegaMenuManagerOutput & {
    identifier: TabIdentifier;
    spotlightSlot?: ReactNode | null;
    contentClassName?: string;
  },
) => {
  const { isMobileView, windowSize } = useMobileView();
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const user = useAppSelector((state) => state.users)?.user;
  const { activeTab, activeSubcategory, identifier } = props;
  const isActive: boolean = activeTab === identifier || activeSubcategory === identifier;
  const isBelowXlBreakpoint = (windowSize?.width ?? 0) < breakpoints.xl;
  const showGoBackButton = (() => {
    /**
     * Check if category is in main menu tabs or if its hidden under "Kategorier" tab
     */
    if (isMobileView && activeSubcategory) {
      const wellnessTabVisible = TAB_VISIBILITY_BREAKPOINT[TabIdentifier.Wellnesscard] < (windowSize?.width ?? 0);
      const giftcardTabVisible = TAB_VISIBILITY_BREAKPOINT[TabIdentifier.Giftcard] < (windowSize?.width ?? 0);

      if (identifier === TabIdentifier.Wellnesscard && wellnessTabVisible) {
        return false;
      }

      if (identifier === TabIdentifier.Giftcard && giftcardTabVisible) {
        return false;
      }

      if (identifier === TabIdentifier.SistaMinuten) {
        return false;
      }

      return true;
    }

    return false;
  })();

  let subCategories: MegaMenuSubCategory[] = undefined;

  const showDefaultSEOContent =
    !props.spotlightSlot &&
    [
      TabIdentifier.Hairdresser,
      TabIdentifier.Massage,
      TabIdentifier.Nails,
      TabIdentifier.Lashes,
      TabIdentifier.Beauty,
      TabIdentifier.Health,
      TabIdentifier.Skincare,
    ].includes(identifier);

  switch (identifier) {
    case TabIdentifier.Hairdresser:
      subCategories = HAIRDRESSER_SUB_CATEGORIES;
      break;
    case TabIdentifier.Massage:
      subCategories = MASSAGE_SUB_CATEGORIES;
      break;
    case TabIdentifier.Nails:
      subCategories = NAILS_SUB_CATEGORIES;
      break;
    case TabIdentifier.Lashes:
      subCategories = LASHES_SUB_CATEGORIES;
      break;
    case TabIdentifier.Beauty:
      subCategories = BEAUTY_SUB_CATEGORIES;
      break;
    case TabIdentifier.Health:
      subCategories = HEALTH_SUB_CATEGORIES;
      break;
    case TabIdentifier.Skincare:
      subCategories = SKINCARE_SUB_CATEGORIES;
      break;
    case TabIdentifier.SistaMinuten:
      subCategories = SISTAMINUTEN_SUB_CATEGORIES;
      break;
    case TabIdentifier.Wellnesscard:
      subCategories = WELLNESSCARD_SUB_CATEGORIES;
      break;
    case TabIdentifier.Giftcard:
      subCategories = GIFTCARD_SUB_CATEGORIES;
      break;
    default:
      subCategories = [];
  }

  const categoryContentClassNames = twMerge(
    'mega-menu-categories-category',
    props.contentClassName,
    !props.spotlightSlot && 'flex flex-col w-full xl:grid xl:grid-cols-[1fr_2fr]',
  );

  return (
    <Category isActive={isActive} identifier={props.identifier} className={categoryContentClassNames}>
      {showGoBackButton && (
        <div className="gap-md mb-md flex items-center">
          <Fab
            size="xs"
            variant="secondary"
            icon={<Icon variant="chevron-left" />}
            aria-label="back"
            onClick={props.onSubCategoryNavigationLeave}
          />
          <h2 className="text-h-l text-fg_primary font-semibold">
            {translate(`Content.${props.identifier}.subnav-title`)}
          </h2>
        </div>
      )}

      {showDefaultSEOContent && (
        <div className="gap-xl mb-3xl flex w-full flex-1 flex-col items-start md:flex-row md:justify-between xl:flex-col xl:justify-normal">
          <div className="space-y-md flex max-w-[400px] flex-col">
            {!isMobileView && (
              <h2 className="text-h-l sm:text-h-2xl text-fg_primary font-semibold">
                {translate(`SEO.${identifier}.title`)}
              </h2>
            )}
            <p className="text-l text-fg_secondary">{translate(`SEO.${identifier}.description`)}</p>
          </div>
          <LinkButton
            leftIcon={<Icon variant="search" color="fg_primary_inverse" />}
            variant="primary"
            to={megaMenuHelpers.buildClientCategoryLink(getDefaultSEOContentCtaLink(identifier), routeMatch)}
            onClick={(e) =>
              handleOnCategoryLinkClick(
                e,
                identifier,
                { to: getDefaultSEOContentCtaLink(identifier), label: translate(`SEO.${identifier}.CTA`) },
                history,
                routeMatch,
                user,
                props.onNavigationLeave,
              )
            }>
            {translate(`SEO.${identifier}.CTA`)}
          </LinkButton>
        </div>
      )}

      {isBelowXlBreakpoint && props.spotlightSlot}
      <div className="gap-2xl md:gap-5xl flex flex-col md:flex-row">
        {subCategories.map((subcategory) => {
          return (
            <section
              key={subcategory.title}
              className={classnames('flex flex-col', isMobileView ? 'gap-2xs md:gap-lg' : 'gap-xl')}>
              <div className={classnames('gap-xs flex flex-col md:flex-row')}>
                {subcategory.icon && (
                  <Icon variant={subcategory.icon} className="hidden md:block" color="fg_primary" size="sm" />
                )}
                <h2 className="text-h-xxs md:text-h-s text-fg_secondary md:text-fg_primary uppercase md:normal-case">
                  {subcategory.title}
                </h2>
              </div>
              <ul className="gap-x-3xl md:pl-3xl grid grid-cols-2 sm:flex">
                {splitCategoryLinksIntoColumns(
                  subcategory.links,
                  props.identifier === TabIdentifier.Giftcard || props.identifier === TabIdentifier.Wellnesscard
                    ? 6
                    : 5,
                ).map((row, idx) => {
                  return (
                    <li key={`${props.identifier}-${idx}`} className="flex flex-col">
                      {row.map((link, key) => {
                        return (
                          <NavLink
                            tabIndex={isActive ? 0 : -1}
                            key={`${props.identifier}-${subcategory.title}-${key}`}
                            onClick={(e) =>
                              handleOnCategoryLinkClick(
                                e,
                                identifier,
                                link,
                                history,
                                routeMatch,
                                user,
                                props.onNavigationLeave,
                              )
                            }
                            to={megaMenuHelpers.buildClientCategoryLink(link.to, routeMatch)}
                            className="block w-full overflow-hidden overflow-ellipsis whitespace-nowrap">
                            {link.label}
                          </NavLink>
                        );
                      })}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
      {!isBelowXlBreakpoint && props.spotlightSlot}
    </Category>
  );
};

const Hairdresser = (props: UseMegaMenuManagerOutput) => {
  return <CategoryContent {...props} identifier={TabIdentifier.Hairdresser} />;
};

const Massage = (props: UseMegaMenuManagerOutput) => {
  return <CategoryContent {...props} identifier={TabIdentifier.Massage} />;
};

const Nails = (props: UseMegaMenuManagerOutput) => {
  return <CategoryContent {...props} identifier={TabIdentifier.Nails} />;
};

const Lashes = (props: UseMegaMenuManagerOutput) => {
  return <CategoryContent {...props} identifier={TabIdentifier.Lashes} />;
};

const Beauty = (props: UseMegaMenuManagerOutput) => {
  return <CategoryContent {...props} identifier={TabIdentifier.Beauty} />;
};

const Skincare = (props: UseMegaMenuManagerOutput) => {
  return <CategoryContent {...props} identifier={TabIdentifier.Skincare} />;
};

const Health = (props: UseMegaMenuManagerOutput) => {
  return <CategoryContent {...props} identifier={TabIdentifier.Health} />;
};

const Wellnesscard = (props: UseMegaMenuManagerOutput) => {
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const user = useAppSelector((state) => state.users)?.user;
  const { isMobileView, windowSize } = useMobileView();
  const wellnessTabVisible = TAB_VISIBILITY_BREAKPOINT[TabIdentifier.Wellnesscard] < (windowSize?.width ?? 0);

  function handleClick(
    e: MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement>,
    { to, label }: { to: string; label: string },
    push: boolean = false,
  ) {
    e.stopPropagation();
    handleOnCategoryLinkClick(
      e,
      TabIdentifier.Wellnesscard,
      { label, to },
      history,
      routeMatch,
      user,
      props.onNavigationLeave,
    );
    if (push)
      history.push({
        pathname: to,
        state: { searchContext: SEARCH_CONTEXT.MEGA_MENU_CATEGORY },
      });
  }

  return (
    <CategoryContent
      {...props}
      identifier={TabIdentifier.Wellnesscard}
      contentClassName="gap-2xl xl:gap-3xl flex flex-col md:grid xl:grid-cols-[1fr_1.3fr]"
      spotlightSlot={
        <section className={classnames('flex flex-col', isMobileView ? 'gap-lg' : 'gap-xl')}>
          {isMobileView && (
            <Fragment>
              {wellnessTabVisible && (
                <div className={classnames('gap-md flex flex-col md:flex-row')}>
                  <Icon variant="wellness-card" size="sm" className="hidden md:block" />
                  <h2 className="text-h-s text-fg_primary">
                    {translate(`Content.${TabIdentifier.Wellnesscard}.title`)}
                  </h2>
                </div>
              )}
              <Link
                className="border-border_regular bg-bg_neutral hover:bg-bg_secondary_hover focus:bg-bg_secondary_pressed focus-within:bg-bg_secondary_pressed relative grid h-full grid-cols-[96px_1fr] place-content-between overflow-hidden rounded-md border"
                to={ROUTE_BUILDER.wellnesscardCheckout()}
                onClick={(e) =>
                  handleClick(e, {
                    to: ROUTE_BUILDER.wellnesscardCheckout(),
                    label: translate(`Content.${TabIdentifier.Wellnesscard}.card-title`),
                  })
                }>
                <div className="flex h-full w-full items-center justify-center">
                  <img
                    src="/images/product-info-wellnesscard-mobile.png"
                    className="h-full w-full bg-[#ddd2e1] object-cover"
                    alt=""
                  />
                </div>
                <div className="pr-xs flex items-center">
                  <div className="space-y-xs p-md flex flex-col">
                    <h3 className="text-l text-fg_primary">
                      {translate(`Content.${TabIdentifier.Wellnesscard}.card-title`)}
                    </h3>
                    <span className="text-s text-fg_secondary">
                      {translate(`Content.${TabIdentifier.Wellnesscard}.card-description`)}
                    </span>
                  </div>
                  <Icon color="fg_secondary" variant="chevron-right" className="ml-auto" />
                </div>
              </Link>
              <NavLink
                className="self-baseline no-underline"
                onClick={(e) =>
                  handleClick(e, {
                    to: APP_ROUTES.GIFTCARD_USE,
                    label: translate(`Content.${TabIdentifier.Wellnesscard}.use`),
                  })
                }
                to={APP_ROUTES.GIFTCARD_USE}>
                {translate(`Content.${TabIdentifier.Wellnesscard}.use`)}
              </NavLink>
            </Fragment>
          )}
          {!isMobileView && (
            <Fragment>
              <div className={classnames('gap-md flex flex-col md:flex-row')}>
                <Icon variant="gift-card" size="sm" className="hidden md:block" />
                <h2 className="text-h-s text-fg_primary">{translate(`Content.${TabIdentifier.Wellnesscard}.title`)}</h2>
              </div>
              <div
                className="gap-xl pl-2xl flex cursor-pointer"
                onClick={(e) => {
                  handleClick(
                    e,
                    {
                      to: ROUTE_BUILDER.wellnesscardCheckout(),
                      label: translate(`Content.${TabIdentifier.Wellnesscard}.buy`),
                    },
                    true,
                  );
                }}
                aria-label={translate(`Content.${TabIdentifier.Wellnesscard}.buy`)}>
                <div className="h-[205px] max-w-[298px] flex-1 overflow-hidden rounded-md">
                  <picture>
                    <source
                      srcSet="/images/product-info-wellnesscard-desktop-2xl.png"
                      media={`(min-width: ${breakpoints['2xl']}px)`}
                    />
                    <source
                      srcSet="/images/product-info-wellnesscard-desktop-lg.png"
                      media={`(min-width: ${breakpoints['md']}px)`}
                    />
                    <img
                      src="/images/product-info-wellnesscard-mobile.png"
                      className="h-full w-full bg-[#ddd2e1] object-cover object-center"
                      alt=""
                    />
                  </picture>
                </div>
                <div className="space-y-xl text-fg_primary flex flex-1 flex-col justify-center">
                  <ul className="space-y-sm">
                    <li className="space-x-xs flex items-center">
                      <Icon variant="checkbox-checked-round" color="fg_positive" size="xs" />
                      <span>{translate(`Content.${TabIdentifier.Wellnesscard}.bullet01`)}</span>
                    </li>
                    <li className="space-x-xs flex items-center">
                      <Icon variant="checkbox-checked-round" color="fg_positive" size="xs" />
                      <span>{translate(`Content.${TabIdentifier.Wellnesscard}.bullet02`)}</span>
                    </li>
                    <li className="space-x-xs flex items-center">
                      <Icon variant="checkbox-checked-round" color="fg_positive" size="xs" />
                      <span>{translate(`Content.${TabIdentifier.Wellnesscard}.bullet03`)}</span>
                    </li>
                  </ul>
                  <div className="gap-x-md z-10 flex flex-shrink-0 items-baseline">
                    <LinkButton
                      onClick={(e) =>
                        handleClick(e, {
                          to: ROUTE_BUILDER.wellnesscardCheckout(),
                          label: translate(`Content.${TabIdentifier.Wellnesscard}.buy`),
                        })
                      }
                      variant="primary"
                      size="lg"
                      to={ROUTE_BUILDER.wellnesscardCheckout()}>
                      {translate(`Content.${TabIdentifier.Wellnesscard}.buy`)}
                    </LinkButton>
                    <NavLink
                      onClick={(e) =>
                        handleClick(e, {
                          to: APP_ROUTES.GIFTCARD_USE,
                          label: translate(`Content.${TabIdentifier.Wellnesscard}.use`),
                        })
                      }
                      to={APP_ROUTES.GIFTCARD_USE}>
                      {translate(`Content.${TabIdentifier.Wellnesscard}.use`)}
                    </NavLink>
                  </div>
                </div>
              </div>
            </Fragment>
          )}
        </section>
      }
    />
  );
};

const Giftcard = (props: UseMegaMenuManagerOutput) => {
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const user = useAppSelector((state) => state.users)?.user;
  const { isMobileView, windowSize } = useMobileView();
  const giftcardTabVisible = TAB_VISIBILITY_BREAKPOINT[TabIdentifier.Giftcard] < (windowSize?.width ?? 0);

  function handleClick(
    e: MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement>,
    { to, label }: { to: string; label: string },
    push: boolean = false,
  ) {
    e.stopPropagation();
    handleOnCategoryLinkClick(
      e,
      TabIdentifier.Giftcard,
      { label, to },
      history,
      routeMatch,
      user,
      props.onNavigationLeave,
    );
    if (push)
      history.push({
        pathname: to,
        state: { searchContext: SEARCH_CONTEXT.MEGA_MENU_CATEGORY },
      });
  }

  return (
    <CategoryContent
      {...props}
      identifier={TabIdentifier.Giftcard}
      contentClassName="gap-2xl xl:gap-3xl flex flex-col xl:grid xl:grid-cols-[1fr_1.3fr]"
      spotlightSlot={
        <section className={classnames('flex flex-col ', isMobileView ? 'gap-lg' : 'gap-xl')}>
          {isMobileView && (
            <Fragment>
              {giftcardTabVisible && (
                <div className={classnames('gap-md flex flex-col md:flex-row')}>
                  <Icon variant="wellness-card" size="sm" className="hidden md:block" />
                  <h2 className="text-h-s text-fg_primary">{translate(`Content.${TabIdentifier.Giftcard}.title`)}</h2>
                </div>
              )}
              <Link
                className="border-border_regular bg-bg_neutral hover:bg-bg_secondary_hover focus:bg-bg_secondary_pressed focus-within:bg-bg_secondary_pressed grid grid-cols-[96px_1fr] place-content-between overflow-hidden rounded-md border"
                to={ROUTE_BUILDER.giftcardCheckout()}
                onClick={(e) =>
                  handleClick(e, {
                    to: ROUTE_BUILDER.giftcardCheckout(),
                    label: translate(`Content.${TabIdentifier.Giftcard}.card-title`),
                  })
                }>
                <div className="flex h-full w-full items-center justify-center">
                  <img
                    src="/images/product-info-giftcard-mobile.png"
                    className="h-full w-full bg-[#ddd2e1] object-cover"
                    alt=""
                  />
                </div>
                <div className="pr-xs flex items-center">
                  <div className="space-y-xs p-md flex flex-col">
                    <h3 className="text-l text-fg_primary">
                      {translate(`Content.${TabIdentifier.Giftcard}.card-title`)}
                    </h3>
                    <span className="text-s text-fg_secondary">
                      <span>{translate(`Content.${TabIdentifier.Giftcard}.card-description`)}</span>
                    </span>
                  </div>
                  <Icon color="fg_secondary" variant="chevron-right" className="ml-auto" />
                </div>
              </Link>
              <NavLink
                className="self-baseline no-underline"
                onClick={(e) =>
                  handleClick(e, {
                    to: APP_ROUTES.GIFTCARD_USE,
                    label: translate(`Content.${TabIdentifier.Giftcard}.use`),
                  })
                }
                to={APP_ROUTES.GIFTCARD_USE}>
                {translate(`Content.${TabIdentifier.Giftcard}.use`)}
              </NavLink>
            </Fragment>
          )}
          {!isMobileView && (
            <Fragment>
              <div className={classnames('gap-md flex flex-col md:flex-row')}>
                <Icon variant="gift-card" size="sm" className="hidden md:block" />
                <h2 className="text-h-s text-fg_primary">{translate(`Content.${TabIdentifier.Giftcard}.title`)}</h2>
              </div>
              <div
                className="gap-xl pl-2xl flex cursor-pointer"
                onClick={(e) => {
                  handleClick(
                    e,
                    {
                      to: ROUTE_BUILDER.giftcardCheckout(),
                      label: translate(`Content.${TabIdentifier.Giftcard}.buy`),
                    },
                    true,
                  );
                }}>
                <div className="h-[205px] max-w-[298px] flex-1 overflow-hidden rounded-md">
                  <picture>
                    <source
                      srcSet="/images/product-info-giftcard-desktop-2xl.png"
                      media={`(min-width: ${breakpoints['2xl']}px)`}
                    />
                    <source
                      srcSet="/images/product-info-giftcard-desktop-lg.png"
                      media={`(min-width: ${breakpoints['md']}px)`}
                    />
                    <img
                      src="/images/product-info-giftcard-mobile.png"
                      className="h-full w-full bg-[#ddd2e1] object-cover"
                      alt=""
                    />
                  </picture>
                </div>
                <div className="space-y-xl text-fg_primary flex flex-1 flex-col justify-center">
                  <ul className="space-y-sm">
                    <li className="space-x-xs flex items-center">
                      <Icon variant="checkbox-checked-round" color="fg_positive" size="xs" />
                      <span>{translate(`Content.${TabIdentifier.Giftcard}.bullet01`)}</span>
                    </li>
                    <li className="space-x-xs flex items-center">
                      <Icon variant="checkbox-checked-round" color="fg_positive" size="xs" />
                      <span>{translate(`Content.${TabIdentifier.Giftcard}.bullet02`)}</span>
                    </li>
                    <li className="space-x-xs flex items-center">
                      <Icon variant="checkbox-checked-round" color="fg_positive" size="xs" />
                      <span>{translate(`Content.${TabIdentifier.Giftcard}.bullet03`)}</span>
                    </li>
                  </ul>
                  <div className="gap-x-md z-10 flex flex-shrink-0 items-baseline">
                    <LinkButton
                      variant="primary"
                      size="lg"
                      to={ROUTE_BUILDER.giftcardCheckout()}
                      onClick={(e) =>
                        handleClick(e, {
                          label: translate(`Content.${TabIdentifier.Giftcard}.buy`),
                          to: ROUTE_BUILDER.giftcardCheckout(),
                        })
                      }>
                      {translate(`Content.${TabIdentifier.Giftcard}.buy`)}
                    </LinkButton>
                    <NavLink
                      className="no-underline"
                      to={APP_ROUTES.GIFTCARD_USE}
                      onClick={(e) => {
                        handleClick(e, {
                          label: translate(`Content.${TabIdentifier.Giftcard}.use`),
                          to: APP_ROUTES.GIFTCARD_USE,
                        });
                      }}>
                      {translate(`Content.${TabIdentifier.Giftcard}.use`)}
                    </NavLink>
                  </div>
                </div>
              </div>
            </Fragment>
          )}
        </section>
      }
    />
  );
};

const SistaMinuten = (props: UseMegaMenuManagerOutput) => {
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const user = useAppSelector((state) => state.users)?.user;
  const { isMobileView } = useMobileView();

  return (
    <CategoryContent
      {...props}
      identifier={TabIdentifier.SistaMinuten}
      contentClassName="gap-2xl xl:gap-3xl flex flex-col xl:grid xl:grid-cols-[1fr_1.3fr]"
      spotlightSlot={
        <section className={classnames('flex flex-col', isMobileView ? 'gap-lg' : 'gap-xl')}>
          {!isMobileView && (
            <div className={classnames('gap-md flex flex-col md:flex-row')}>
              <Icon variant="smt" size="sm" className="hidden md:block" />
              <h2 className="text-h-xxs md:text-h-s text-fg_secondary md:text-fg_primary uppercase md:normal-case">
                {translate(`Content.${TabIdentifier.SistaMinuten}.title`)}
              </h2>
            </div>
          )}
          {isMobileView && (
            <Fragment>
              <div className={classnames('gap-md flex flex-col md:flex-row')}>
                <Icon variant="wellness-card" size="sm" className="hidden md:block" />
                <h2 className="text-h-s text-fg_primary">{translate(`Content.${TabIdentifier.SistaMinuten}.title`)}</h2>
              </div>
              <Link
                className="border-border_regular bg-bg_neutral hover:bg-bg_secondary_hover focus:bg-bg_secondary_pressed focus-within:bg-bg_secondary_pressed grid grid-cols-[96px_1fr] place-content-between overflow-hidden rounded-md border"
                to={ROUTE_BUILDER.sistaMinutenSerp(DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION)}
                onClick={(e) =>
                  handleOnCategoryLinkClick(
                    e,
                    TabIdentifier.SistaMinuten,
                    {
                      label: translate(`Content.${TabIdentifier.SistaMinuten}.card-title`),
                      to: ROUTE_BUILDER.sistaMinutenSerp(DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION),
                    },
                    history,
                    routeMatch,
                    user,
                    props.onNavigationLeave,
                  )
                }>
                <div
                  className="flex h-full w-full items-center justify-center"
                  style={{
                    backgroundColor: '#E5BD2D',
                    background:
                      'radial-gradient(circle,rgba(229, 189, 45, 1) 55%, rgba(229, 189, 45, 1) 55%, rgba(237, 196, 51, 1) 55%, rgba(237, 196, 51, 1) 85%, rgba(245, 204, 59, 1) 85%, rgba(245, 204, 59, 1) 85%, rgba(245, 204, 59, 1) 100%)',
                  }}>
                  <Icon variant="smt" color="white" style={{ width: 40, height: 40 }} />
                </div>
                <div className="pr-xs flex items-center">
                  <div className="space-y-xs p-md flex flex-col">
                    <h3 className="text-l text-fg_primary">
                      {translate(`Content.${TabIdentifier.SistaMinuten}.card-title`)}
                    </h3>
                    <span className="text-s text-fg_secondary">
                      {translate(`Content.${TabIdentifier.SistaMinuten}.card-description`)}
                    </span>
                  </div>
                  <Icon color="fg_secondary" variant="chevron-right" className="ml-auto" />
                </div>
              </Link>
            </Fragment>
          )}
          {!isMobileView && (
            <div
              className="gap-xl pl-2xl flex cursor-pointer"
              onClick={(e) => {
                handleOnCategoryLinkClick(
                  e,
                  TabIdentifier.SistaMinuten,
                  {
                    label: translate(`Content.${TabIdentifier.SistaMinuten}.cta`),
                    to: ROUTE_BUILDER.sistaMinutenSerp(),
                  },
                  history,
                  routeMatch,
                  user,
                  props.onNavigationLeave,
                );
                history.push({
                  pathname: ROUTE_BUILDER.sistaMinutenSerp(),
                  state: { searchContext: SEARCH_CONTEXT.MEGA_MENU_CATEGORY },
                });
              }}>
              <div className="h-[205px] w-full max-w-[298px] flex-shrink-0 overflow-hidden rounded-md">
                <div
                  className="flex h-full w-full items-center justify-center"
                  style={{
                    backgroundColor: '#E5BD2D',
                    background:
                      'radial-gradient(circle,rgba(229, 189, 45, 1) 55%, rgba(229, 189, 45, 1) 55%, rgba(237, 196, 51, 1) 55%, rgba(237, 196, 51, 1) 85%, rgba(245, 204, 59, 1) 85%, rgba(245, 204, 59, 1) 85%, rgba(245, 204, 59, 1) 100%)',
                  }}>
                  <Icon variant="smt" color="white" style={{ width: 100, height: 100 }} />
                </div>
              </div>
              <div className="space-y-xl text-fg_primary flex flex-shrink-0 flex-col justify-center">
                <ul className="space-y-sm">
                  <li className="space-x-xs flex items-center">
                    <Icon variant="checkbox-checked-round" color="fg_positive" size="xs" />
                    <span>{translate(`Content.${TabIdentifier.SistaMinuten}.bullet01`)}</span>
                  </li>
                  <li className="space-x-xs flex items-center">
                    <Icon variant="checkbox-checked-round" color="fg_positive" size="xs" />
                    <span>{translate(`Content.${TabIdentifier.SistaMinuten}.bullet02`)}</span>
                  </li>
                  <li className="space-x-xs flex items-center">
                    <Icon variant="checkbox-checked-round" color="fg_positive" size="xs" />
                    <span>{translate(`Content.${TabIdentifier.SistaMinuten}.bullet03`)}</span>
                  </li>
                </ul>
                <div className="flex flex-shrink-0 items-baseline">
                  <LinkButton
                    variant="primary"
                    size="lg"
                    to={ROUTE_BUILDER.sistaMinutenSerp()}
                    onClick={(e) =>
                      handleOnCategoryLinkClick(
                        e,
                        TabIdentifier.SistaMinuten,
                        {
                          label: translate(`Content.${TabIdentifier.SistaMinuten}.cta`),
                          to: ROUTE_BUILDER.sistaMinutenSerp(),
                        },
                        history,
                        routeMatch,
                        user,
                        props.onNavigationLeave,
                      )
                    }>
                    {translate(`Content.${TabIdentifier.SistaMinuten}.cta`)}
                  </LinkButton>
                </div>
              </div>
            </div>
          )}
        </section>
      }
    />
  );
};
const More = (props: UseMegaMenuManagerOutput) => {
  const [activeCategoryKey, setActiveCategoryKey] = useState<MoreCategoryKey | undefined>('A');
  const { isMobileView, windowSize } = useMobileView();
  const container = useRef<HTMLDivElement>(null);
  const routeMatch = useRouteMatch();
  const history = useHistory();
  const user = useAppSelector((state) => state.users)?.user;
  const { activeTab, activeSubcategory } = props;
  const isActive = activeTab === TabIdentifier.More || activeSubcategory === TabIdentifier.More;
  const isBelow1439px = (windowSize?.width ?? 0) < 1439;

  useEffect(() => {
    setActiveCategoryKey('A');
  }, [isActive]);

  function paginate() {
    const recursiveFind = (key: string, direction: 'next' | 'prev') => {
      const idx = MORE_CATEGORY_KEYS.findIndex((_key) => _key === key);

      if (idx === -1) {
        setActiveCategoryKey(MORE_CATEGORY_KEYS[0]);
        scrollToCategory(MORE_CATEGORY_KEYS[0]);
        return;
      }

      if (idx === MORE_CATEGORY_KEYS.length - 1 && direction === 'next') {
        setActiveCategoryKey(MORE_CATEGORY_KEYS[0]);
        scrollToCategory(MORE_CATEGORY_KEYS[0]);
        return;
      }

      if (idx === 0 && direction === 'prev') {
        setActiveCategoryKey(MORE_CATEGORY_KEYS[MORE_CATEGORY_KEYS.length - 1]);
        scrollToCategory(MORE_CATEGORY_KEYS[MORE_CATEGORY_KEYS.length - 1]);
        return;
      }

      const to = MORE_CATEGORY_KEYS?.[direction === 'next' ? idx + 1 : idx - 1];

      // Check if the category exists in MORE_CATEGORIES_GROUPED and has items
      if (to && MORE_CATEGORIES_GROUPED[to]?.length) {
        setActiveCategoryKey(to);
        scrollToCategory(to);
        return;
      }

      if (!to) {
        // Reached the end of the keys without finding a valid category
        return undefined;
      }

      return recursiveFind(to, direction);
    };

    return {
      next: (e: MouseEvent<HTMLButtonElement>) => {
        e.currentTarget?.blur();
        recursiveFind(activeCategoryKey, 'next');
      },
      prev: (e: MouseEvent<HTMLButtonElement>) => {
        e.currentTarget?.blur();
        recursiveFind(activeCategoryKey, 'prev');
      },
      goto: (key: MoreCategoryKey) => {
        setActiveCategoryKey(key);
        scrollToCategory(key);
      },
    };
  }

  function scrollToCategory(key: string) {
    document?.getElementById?.(key)?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  }

  return (
    <CategoryContent
      {...props}
      identifier={TabIdentifier.More}
      spotlightSlot={
        <div className={classnames('flex w-full flex-col', isMobileView ? 'gap-lg' : 'gap-xl')}>
          {!isBelow1439px && (
            <div className="space-y-2xl flex flex-col">
              <div className="flex items-center justify-between">
                <div className={classnames('gap-md flex flex-col md:flex-row')}>
                  <h2 className="text-h-xxs md:text-h-s text-fg_secondary md:text-fg_primary uppercase md:normal-case">
                    {translate(`Content.${TabIdentifier.More}.title`)}
                  </h2>
                </div>
                <div className="bg-surface_l4 p-xs flex items-center rounded-lg">
                  {/* Iterate over MORE_CATEGORY_KEYS */}
                  {MORE_CATEGORY_KEYS.map((key) => {
                    // Only render buttons for categories that actually have items
                    if (!MORE_CATEGORIES_GROUPED[key]?.length) {
                      return null;
                    }
                    return (
                      <button
                        key={key}
                        onClick={() => paginate().goto(key)}
                        className={classnames(
                          'h-2xl w-2xl text-l focus-within:outline-information-500 rounded-full',
                          activeCategoryKey === key
                            ? 'bg-bg_primary text-fg_primary_inverse outline-none'
                            : 'text-fg_secondary hover:bg-bg_secondary_hover',
                        )}>
                        {key.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
                <div className="space-x-sm flex items-center">
                  <Fab size="md" onClick={paginate().prev} variant="secondary" icon={<Icon variant="arrow-left" />} />
                  <Fab size="md" onClick={paginate().next} variant="secondary" icon={<Icon variant="arrow-right" />} />
                </div>
              </div>

              <div ref={container} className="no-scrollbar gap-md flex items-start overflow-x-scroll">
                {MORE_CATEGORY_KEYS.map((key) => {
                  const categoryItems = MORE_CATEGORIES_GROUPED[key];
                  if (!categoryItems?.length) {
                    /**
                     * If there are no items in the category, we skip rendering it.
                     */
                    return null;
                  }
                  return (
                    <section
                      key={key}
                      id={key}
                      className="border-border_regular pl-xl h-full border-l first:border-none">
                      <h2 className="text-h-s text-fg_primary px-sm inline-block">{key.toUpperCase()}</h2>
                      <div className="gap-md pl-sm pt-sm flex">
                        {splitCategoryLinksIntoColumns(categoryItems).map((row, idx) => {
                          return (
                            <div key={`${row[0]?.label}-${idx}`} className="flex flex-col">
                              {row.map((link, linkIdx) => {
                                return (
                                  <NavLink
                                    key={`${link.label}-${link.to}-${linkIdx}`}
                                    onClick={(e) =>
                                      handleOnCategoryLinkClick(
                                        e,
                                        TabIdentifier.More,
                                        link,
                                        history,
                                        routeMatch,
                                        user,
                                        props.onNavigationLeave,
                                      )
                                    }
                                    to={link.to}
                                    className="block w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
                                    {link.label}
                                  </NavLink>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>
          )}

          {isBelow1439px &&
            MORE_CATEGORY_KEYS.map((key) => {
              const categoryItems = MORE_CATEGORIES_GROUPED[key];
              if (!categoryItems?.length) {
                return null;
              }
              return (
                <section key={key} className="last-of-type:pb-3xl flex flex-col">
                  <h2 className="text-h-s text-fg_primary bg-surface_l4 rounded-xs px-sm inline-block">
                    {key.toUpperCase()}
                  </h2>
                  <div
                    className="gap-md pl-sm pt-sm grid"
                    style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                    {categoryItems.map((link) => {
                      return (
                        <NavLink
                          key={link.label}
                          to={megaMenuHelpers.buildClientCategoryLink(link.to, routeMatch)}
                          onClick={(e) =>
                            handleOnCategoryLinkClick(
                              e,
                              TabIdentifier.More,
                              link,
                              history,
                              routeMatch,
                              user,
                              props.onNavigationLeave,
                            )
                          }>
                          {link.label}
                        </NavLink>
                      );
                    })}
                  </div>
                </section>
              );
            })}
        </div>
      }
    />
  );
};

const MobileNavigation = (props: UseMegaMenuManagerOutput) => {
  const routeMatch = useRouteMatch();
  const { windowSize } = useMobileView();
  const { activeTab, activeSubcategory } = props;
  const isActive = activeTab === TabIdentifier.Categories;
  if (activeSubcategory !== TabIdentifier.Categories) return null;

  return (
    <Category isActive={isActive} identifier={TabIdentifier.Categories}>
      <div className="flex max-h-[90vh] w-full flex-col">
        {props.tabs.map((item) => {
          const width = windowSize?.width ?? 0;
          const breakpointShowWellness = TAB_VISIBILITY_BREAKPOINT[TabIdentifier.Wellnesscard];
          const breakpointShowGiftcard = TAB_VISIBILITY_BREAKPOINT[TabIdentifier.Giftcard];

          if (item.identifier === TabIdentifier.Categories) return null;
          if (item.identifier === TabIdentifier.Giftcard && width > breakpointShowGiftcard) return null;
          if (item.identifier === TabIdentifier.Wellnesscard && width > breakpointShowWellness) return null;
          if (item.identifier === TabIdentifier.SistaMinuten) return null;

          return (
            <button
              key={item.identifier}
              className="py-xs flex w-full justify-between"
              onClick={() => props.onSubCategoryNavigationOpen(item.identifier, routeMatch)}>
              <span className="flex items-center">
                <Icon variant={item.icon} className="mr-sm" color="fg_primary" />
                {item.label}
              </span>
              <Icon variant="chevron-right" className="ml-auto" />
            </button>
          );
        })}
      </div>
    </Category>
  );
};

Category.Hairdresser = Hairdresser;
Category.Massage = Massage;
Category.Nails = Nails;
Category.Lashes = Lashes;
Category.Beauty = Beauty;
Category.Skincare = Skincare;
Category.Health = Health;
Category.More = More;
Category.Wellnesscard = Wellnesscard;
Category.Giftcard = Giftcard;
Category.SistaMinuten = SistaMinuten;
Category.MobileNavigation = MobileNavigation;

export default Category;
